'use client'

import { floorGap } from '@/utils/offer-floor-gap'
import { useEffect, useState } from 'react'
import useSWR, { useSWRConfig } from 'swr'
import axios from 'axios'
import { ArrowRight, Check, ChevronDown, Search, Zap, TriangleAlert } from 'lucide-react'
import { useCollectionOffers } from '@/utils/hooks/useOffers'
import useIsHydrated from '@/utils/hooks/useIsHydrated'
import useLoggedIn from '@/utils/hooks/useLoggedIn'
import { offersEnabledFor, type Offer } from '@/utils/offers'
import { instantSellOffers, eligibleSellItems } from '@/utils/instant-sell'
import { API_URL } from '@/utils/constants'
import type { Inscription } from '@/utils/types/inscription'
import Wallet from '@/utils/wallet'
import event from '@/utils/event'
import InscriptionContent from '@/components/inscription-content'
import OfferDialog from './offer-dialog'
import CollectionIcon from '@/components/collection-icon'
import useCollection from '@/utils/hooks/useCollection'
import useCollectionStats from '@/utils/hooks/useCollectionStats'
import useBidReference from '@/utils/hooks/useBidReference'
import dialogStyles from './offer-dialog.module.css'
import ReviewOfferModal from './review-offer-modal'
import OfferUsd from './offer-usd'
import BitcoinIcon from '@/icons/bitcoin-filled'
import ScrollLoadMore from '@/components/scroll-load-more'
import OfferMessage from './offer-message'
import { bookBtc } from './collection-offer-book'
import styles from './instant-sell.module.css'
import tradeStyles from './trade-panel.module.css'
import InstantSellButton from './instant-sell-button'

const inventoryFetcher = async ([, address]: readonly string[]): Promise<Inscription[]> => {
	const { data } = await axios.get(`${API_URL}/wallet/${address}/inscriptions`)
	if (!Array.isArray(data)) throw new Error('Inventory unavailable')
	// The address-scoped inventory omits owner; explicit conflicting owners remain excluded.
	return data.map((item: Inscription) => ({ ...item, owner: item.owner || address }))
}

export default function InstantSell({
	slug,
	name,
	compact = false,
	buyRow = false,
	showAmount = false,
	hideUnavailable = false,
	className
}: {
	slug: string
	name?: string | null
	compact?: boolean
	buyRow?: boolean
	showAmount?: boolean
	hideUnavailable?: boolean
	className?: string
}) {
	const hydrated = useIsHydrated()
	const loggedIn = useLoggedIn()
	const sellerAddress = loggedIn ? Wallet.getTaprootAddress() : ''
	const {
		data,
		error: offersError,
		mutate
	} = useCollectionOffers(hydrated && offersEnabledFor(slug) ? slug : null)
	const [open, setOpen] = useState(false)
	const [now, setNow] = useState(Date.now)
	useEffect(() => {
		const timer = setInterval(() => setNow(Date.now()), 1000)
		return () => clearInterval(timer)
	}, [])
	const offers = offersError
		? []
		: instantSellOffers(data, slug, now).filter(
				(o) => o.buyer_address !== sellerAddress && o.buyer_payment_address !== sellerAddress
		  )
	const top = offers.find((o) => o.scope === 'collection')
	if (!hydrated || !offersEnabledFor(slug) || ((buyRow || hideUnavailable) && !offers.length)) return null
	return (
		<>
			{compact ? (
				<InstantSellButton
					type="button"
					className={[buyRow ? tradeStyles.buyRow : '', className].filter(Boolean).join(' ')}
					disabled={!offers.length}
					onClick={() => {
						void mutate()
						setOpen(true)
					}}
					aria-label={`Instant sell ${name || slug}`}
				>
					{(buyRow || showAmount) && offers.length ? `Instant Sell • ${bookBtc((top || offers[0]).price_sats)} BTC` : 'Instant Sell'}
				</InstantSellButton>
			) : (
				top && (
					<button
						type="button"
						className={compact ? styles.compact : styles.card}
						onClick={() => {
							void mutate()
							setOpen(true)
						}}
						aria-label={`Instant sell ${name || slug}`}
					>
						<span className={styles.bolt}>
							<Zap size={22} aria-hidden="true" />
						</span>
						<strong>Instant sell</strong>
						<span className={styles.topLabel}>Top offer</span>
						<span className={styles.price}>{bookBtc(top.price_sats)} BTC</span>
						<OfferUsd sats={top.price_sats} />
						<span className={styles.viewAll}>
							View offers ({offers.length}) <ArrowRight size={14} aria-hidden="true" />
						</span>
					</button>
				)
			)}
			{open && (
				<InstantSellDialog slug={slug} name={name} offers={offers} onClose={() => setOpen(false)} />
			)}
		</>
	)
}

export function InstantSellDialog({
	slug,
	name,
	offers,
	onClose
}: {
	slug: string
	name?: string | null
	offers: Offer[]
	onClose: () => void
}) {
	const loggedIn = useLoggedIn()
	const address = loggedIn ? Wallet.getTaprootAddress() : ''
	const [connecting, setConnecting] = useState(false)
	// The review (and its Sold receipt) lives here, above the keyed picker, so
	// a wallet re-login during signing cannot remount it away mid-sale.
	const [review, setReview] = useState<{ offer: Offer; itemId: string } | null>(null)
	const { mutate } = useSWRConfig()
	useEffect(() => {
		const resume = () => setConnecting(false)
		event.listen('connect-wallet-closed', resume)
		return () => event.removeListener('connect-wallet-closed', resume)
	}, [])
	if (connecting) return null
	if (review)
		return (
			<ReviewOfferModal
				key={review.offer.id}
				offer={review.offer}
				itemId={review.itemId}
				surface="instant_sell"
				onClose={onClose}
				onChanged={() => {
					void mutate(['offers:collection', slug])
					void mutate((key) => Array.isArray(key) && String(key[0]).startsWith('offers:'))
				}}
			/>
		)
	return (
		<SellPicker
			key={address || 'guest'}
			slug={slug}
			name={name}
			offers={offers}
			address={address || ''}
			onClose={onClose}
			onReview={setReview}
			onConnect={() => {
				setConnecting(true)
				event.emit('command', { id: 'connect-wallet' })
			}}
		/>
	)
}

function SellPicker({
	slug,
	name,
	offers,
	address,
	onClose,
	onConnect,
	onReview
}: {
	slug: string
	name?: string | null
	offers: Offer[]
	address: string
	onClose: () => void
	onConnect: () => void
	onReview: (review: { offer: Offer; itemId: string }) => void
}) {
	const {
		data: items = [],
		isLoading,
		error,
		mutate
	} = useSWR(address ? ['offers:sell-inventory', address] : null, inventoryFetcher, {
		revalidateOnFocus: true
	})
	const { data: collection } = useCollection(slug)
	const { data: collectionStats } = useCollectionStats(slug)
	const floorReference = useBidReference(slug, Number(collectionStats?.floor_price) || null)
	const floorPill = (sats: number) => {
		if (!floorReference.sats || floorReference.sats <= 0) return null
		// 30% or more under the floor turns the whole pill red, so a deep discount stands out in the list.
		const deep = floorGap(sats, floorReference.sats)?.deep
		return (
			<span className={`${styles.floorPill} ${deep ? styles.floorPillDanger : ''}`} data-deep-discount={deep || undefined}>
				{deep && <TriangleAlert size={10} aria-hidden="true" />}
				<span className={sats < floorReference.sats ? styles.belowFloorPercent : undefined}>
					{Math.round(Math.abs(1 - sats / floorReference.sats) * 100)}%
				</span>{' '}
				{sats <= floorReference.sats ? 'Below Floor' : 'Above Floor'}
			</span>
		)
	}
	const [filter, setFilter] = useState<'collection' | 'trait'>('collection')
	const [offerId, setOfferId] = useState<string | null>(null)
	const [itemId, setItemId] = useState<string | null>(null)
	const available = offers.filter((o) => o.buyer_address !== address)
	const selected = available.find((o) => o.id === offerId)
	const eligible = selected ? eligibleSellItems(items, selected, address) : []
	const selectedItem = eligible.find((item) => item.id === itemId)
	const rows = available.filter((o) => o.scope === filter)
	const displayedOffer = selectedItem && selected ? selected : [...rows].sort((a, b) => b.price_sats - a.price_sats)[0]
	return (
		<OfferDialog
			title="Instant Sell"
            headerPrefix={<div className={dialogStyles.bidIdentity}>{collection && <CollectionIcon collection={collection} />}<span>{name || collection?.name || slug}</span></div>}
            headerAccessory={<div className={styles.headerTabs} role="group" aria-label="Offer types">
				{(['collection', 'trait'] as const).map((kind) => (
					<button
						type="button"
						key={kind}
						aria-pressed={filter === kind}
						onClick={() => {
							setFilter(kind)
							setOfferId(null)
							setItemId(null)
						}}
					>
						{kind === 'collection' ? 'Collection' : 'Traits'}{' '}
						<small>{available.filter((o) => o.scope === kind).length}</small>
					</button>
				))}
			</div>}
			description=""
			onClose={onClose}
			footer={
				selected && address ? (
					<button
						className={styles.primary}
						disabled={!selectedItem || !!error || isLoading}
						onClick={() => selectedItem && onReview({ offer: selected, itemId: selectedItem.id })}
					>
						Review sale · {bookBtc(selected.price_sats)} BTC
					</button>
				) : undefined
			}
		>
			{displayedOffer && (
                <div className={styles.priceCard} tabIndex={0} onKeyDown={event => {
                        if (event.target !== event.currentTarget || !['Enter', ' '].includes(event.key)) return
                        event.preventDefault()
                        event.currentTarget.querySelector<HTMLButtonElement>('[data-item-picker-trigger]')?.click()
                    }} onClick={event => {
                        if ((event.target as HTMLElement).closest('button, input, select, a')) return
                        event.currentTarget.querySelector<HTMLButtonElement>('[data-item-picker-trigger]')?.click()
                    }}>
                    <div className={styles.priceIdentity}><span className={styles.priceBolt}><Zap size={22} className="fill-current" aria-hidden="true" /></span><span className={styles.priceCaption}>{selectedItem ? 'Selected offer' : 'Top offer'}</span>{floorPill(displayedOffer.price_sats)}</div>
                    <div className={styles.priceAmounts}>
                        <strong className={styles.priceFigure}><BitcoinIcon className="size-4" />{bookBtc(displayedOffer.price_sats)}</strong>
                        <OfferUsd sats={displayedOffer.price_sats} />
                    </div>
                    {address && <SellItemDropdown defaultOpen slug={slug} items={eligibleSellItems(items, displayedOffer, address)} selectedId={offerId === displayedOffer.id ? itemId : null} onSelect={(id) => { setOfferId(displayedOffer.id); setItemId(id) }} />}

                </div>
            )}
            {selectedItem && <div className={styles.selectedSaleItem}>
                <span className={styles.thumbnail}><InscriptionContent inscription={selectedItem} smallCard /></span>
                <div><strong>{selectedItem.meta?.name || `Inscription #${selectedItem.num}`}</strong><small>Instant sell {selectedItem.meta?.name || `Inscription #${selectedItem.num}`}</small></div>
            </div>}
            {!address && (
				<div className={styles.connect}>
					<span>Connect your wallet to see which items you can sell.</span>
					<button type="button" className={styles.primary} onClick={onConnect}>
						Connect wallet
					</button>
				</div>
			)}


			{offerId && !selected && (
				<p role="status">That offer is no longer available. Choose another offer.</p>
			)}
			{error ? (
				<p role="alert">
					Your items could not be loaded.{' '}
					<button className={styles.back} onClick={() => void mutate()}>
						Retry
					</button>
				</p>
			) : isLoading ? (
				<p role="status">Loading your items…</p>
			) : null}
			{!rows.length && (
				<p className={styles.empty}>
					No {filter === 'trait' ? 'trait' : 'collection'} offers available to sell into.
				</p>
			)}
			<div className={styles.offers}>
				{(selectedItem ? [] : rows).filter(offer => offer.id !== displayedOffer?.id).sort((a, b) => b.price_sats - a.price_sats).map((offer) => {
					const eligibleItems = eligibleSellItems(items, offer, address)
					const label =
						offer.scope === 'trait' ? `${offer.trait_type}: ${offer.trait_value}` : 'Collection offer'
					return (
						<section
							key={offer.id}
							className={styles.offerListEntry}
							aria-label={`${label} · ${bookBtc(offer.price_sats)} BTC`}
						>
							<div className={styles.priceCard} tabIndex={0} onKeyDown={event => {
                        if (event.target !== event.currentTarget || !['Enter', ' '].includes(event.key)) return
                        event.preventDefault()
                        event.currentTarget.querySelector<HTMLButtonElement>('[data-item-picker-trigger]')?.click()
                    }} onClick={event => {
                        if ((event.target as HTMLElement).closest('button, input, select, a')) return
                        event.currentTarget.querySelector<HTMLButtonElement>('[data-item-picker-trigger]')?.click()
                    }}>
                                <div className={styles.priceIdentity}><span className={styles.priceCaption}>{label}</span>{floorPill(offer.price_sats)}</div>
                                <div className={styles.priceAmounts}>
                                    <strong className={styles.priceFigure}><BitcoinIcon className="size-4" />{bookBtc(offer.price_sats)}</strong>
                                    <OfferUsd sats={offer.price_sats} />
                                </div>
							{address && <SellItemDropdown slug={slug} items={eligibleItems} selectedId={offerId === offer.id ? itemId : null} onSelect={(id) => { setOfferId(offer.id); setItemId(id) }} />}</div>
							<OfferMessage offer={offer} message={offer.message} />

						</section>
					)
				})}
			</div>
		</OfferDialog>
	)
}

function SellItemDropdown({ defaultOpen = false, slug, items, selectedId, onSelect }: { defaultOpen?: boolean; slug: string; items: Inscription[]; selectedId: string | null; onSelect: (id: string) => void }) {
 const [open, setOpen] = useState(defaultOpen)
 const [search, setSearch] = useState('')
 const [count, setCount] = useState(20)
 const [sort, setSort] = useState(slug === 'bitmap' ? 'number-desc' : 'number-asc')
 const itemNumber = (item: Inscription) => slug === 'bitmap' ? Number(item.meta?.name?.match(/^(\d+)\.bitmap/i)?.[1] ?? item.num) : Number(item.num)
 const filtered = items.filter(item => `${item.meta?.name || ''} ${item.num} ${item.id}`.toLowerCase().includes(search.toLowerCase())).sort((a, b) => sort === 'name' ? (a.meta?.name || String(a.num)).localeCompare(b.meta?.name || String(b.num), undefined, { numeric: true }) : sort === 'number-desc' ? itemNumber(b) - itemNumber(a) : itemNumber(a) - itemNumber(b))
 return <div className={styles.inlinePicker}>
  <button type="button" className={styles.dropdownTrigger} data-item-picker-trigger aria-expanded={open} onClick={() => setOpen(value => !value)}><Zap size={13} className="fill-current" aria-hidden="true" /><span>Instant Sell</span><ChevronDown size={14} /></button>
  {open && <div className={styles.inlineDropdown} onClick={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()}>
   <p className={styles.pickerHint}>Select the item you want to sell below</p>
   <div className={styles.itemSearchRow}>
   <div className={styles.itemSearch}><Search size={16} /><input aria-label="Search your items" placeholder="Search items" value={search} onChange={event => { setSearch(event.target.value); setCount(20) }} /></div>
    <select className={styles.itemSort} aria-label="Sort your items" value={sort} onChange={event => { setSort(event.target.value); setCount(20) }}><option value="number-desc">Highest number</option><option value="number-asc">Lowest number</option><option value="name">Name A–Z</option></select>
   </div>
   <div className={styles.dropdownItems}>
    {filtered.slice(0, count).map(item => <button type="button" key={item.id} className={styles.item} aria-pressed={selectedId === item.id} onClick={() => { onSelect(item.id); setOpen(false) }}>
     <span className={styles.thumbnail}><InscriptionContent inscription={item} smallCard /></span>
     <span><strong>{item.meta?.name || `Inscription #${item.num}`}</strong><small>{item.escrow ? 'Listed' : 'In your wallet'}</small></span>
     {selectedId === item.id && <Check size={16} />}
    </button>)}
    {!filtered.length && <p className={styles.empty}>No matching items.</p>}
    <ScrollLoadMore hasMore={filtered.length > count} itemCount={Math.min(count, filtered.length)} onLoadMore={() => setCount(value => value + 20)} />
   </div>
  </div>}
 </div>
}
